import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle } from "../core/oda";
import { setDynBlockProperty } from "../blocks/dynamic";
// Import through the barrel, not ./selection/SelectionService: Grid pulls this command
// in (via the dynamic block handles), which closes an import cycle back to the selection
// module. The barrel evaluates SelectionSet before SelectionService, so the singleton's
// constructor finds it initialized; a direct import hits a TDZ error at startup.
import { selectionService } from "../selection";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

/** One property value change on a dynamic block reference. */
export interface DynBlockPropertyChange {
  /** Dynamic property name as authored in AutoCAD, e.g. "Etat d'inversion1". */
  propertyName: string;
  oldValue: string;
  newValue: string;
}

export interface SetDynBlockPropertyParams {
  /** Handle of the block reference — the entity shown in the activity feed. */
  blockHandle: string;
  /**
   * Changes applied together. Dragging an XY point moves two properties at once and
   * must undo as a single step, hence a list rather than one property per command.
   */
  changes: DynBlockPropertyChange[];
}

/**
 * Undo/redo for dynamic block property changes (flip, visibility state, position…).
 *
 * The mutation is already applied by blocks/dynamic.ts before this command is built
 * (same convention as SetPhotoMarkerAttributeCommand): execute() only records the
 * oplog, while undo/redo re-apply the stored values.
 *
 * Changing a property makes ODA re-evaluate the block and swap its anonymous
 * representation, so a plain recordGraphicsModified is not enough — we regenerate.
 */
export class SetDynBlockPropertyCommand implements ICommand {
  private oplogEvents: OplogEmit[] = [];

  constructor(
    private readonly viewer: DRDrawingViewer,
    private readonly params: SetDynBlockPropertyParams,
  ) {}

  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(
      this.viewer,
      this.params.changes.map((change) => ({
        handle: this.params.blockHandle,
        changeKind: "dynBlockProperty",
        oldValue: `${change.propertyName}=${change.oldValue}`,
        newValue: `${change.propertyName}=${change.newValue}`,
      })),
    );
  }

  undo(): void {
    this.apply("oldValue");
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    this.apply("newValue");
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {}

  private apply(key: "oldValue" | "newValue"): void {
    const { module, database } = this.viewer;
    const blockId = findEntityByHandle(
      module,
      database,
      this.params.blockHandle,
    );
    if (!blockId || blockId.isNull()) return;

    for (const change of this.params.changes) {
      setDynBlockProperty(module, blockId, change.propertyName, change[key]);
    }
    deleteObj(blockId);

    // Re-evaluating the block changes its geometry: regenerate, then refresh the
    // selection grid so transform handles follow the new extents.
    this.viewer.forceRegen();
    selectionService.updateGrid();
  }
}
